import { Box } from '@mui/material';
import { getIn, useFormikContext } from 'formik';
import { useEffect, useMemo, useState } from 'react';

import { FormField } from 'components/form/formField/formField';
import { SearchableSelect } from 'components/inputs/select/searchableSelect';
import { SelectItem } from 'components/inputs/select/select';

import { FORMIK_INITIAL_VALUES, STEP_1_FIELDS } from '../../../../keys';
import { KEYS } from './keys';
import { Container, Label, StyledTimeInput, StyledToggleButton, StyledToggleButtonGroup, TimeColon } from './styles';
import { TIMEZONE_OPTIONS } from './timezones';
import { useTimeField } from './useTimeField';
import { parseTimeFromISO } from './utils/time';

interface Props {
  fieldPath: string;
  error?: Record<string, string>;
  label: string;
  disabled?: boolean;
}

export const CustomTimePicker = ({ fieldPath, error, label, disabled = false }: Props) => {
  const [errorText, setErrorText] = useState<string | undefined>(undefined);

  const { values, touched, errors } = useFormikContext<typeof FORMIK_INITIAL_VALUES>();

  const finalPath = useMemo(
    () => ([STEP_1_FIELDS.KYC_SCREEN, STEP_1_FIELDS.OFAC_SCREEN].includes(fieldPath) ? `${fieldPath}.screenedAt` : fieldPath),
    [fieldPath],
  );

  const timeValue = getIn(values, finalPath);
  const timeError = getIn(errors, finalPath);
  const timeTouched = getIn(touched, finalPath);

  useEffect(() => {
    if (error?.[finalPath]) {
      setErrorText(error[finalPath]);
    }

    if (timeError && timeTouched) {
      setErrorText(timeError);
    }

    if (!error?.[finalPath] && !timeError) {
      setErrorText(undefined);
    }
  }, [error, finalPath, timeError, timeTouched]);

  const { localTime, handleChange, handleBlur, getBoundedInputHandler, handleTimezoneChange } = useTimeField(finalPath);

  const parsed = parseTimeFromISO(timeValue || '', TIMEZONE_OPTIONS[0]);

  return (
    <Container>
      <Label>{label}</Label>
      <FormField error={errorText}>
        <Box display="flex" alignItems="center" gap={1}>
          <StyledTimeInput
            type="number"
            inputProps={{ min: 1, max: 12 }}
            value={localTime.hour}
            onChange={(e) => handleChange('hour', e.target.value)}
            onBlur={handleBlur}
            onBeforeInput={getBoundedInputHandler(12)}
            disabled={disabled}
          />
          <TimeColon>:</TimeColon>
          <StyledTimeInput
            type="number"
            value={localTime.minute}
            onChange={(e) => handleChange('minute', e.target.value)}
            onBlur={handleBlur}
            inputProps={{ min: 0, max: 59 }}
            onBeforeInput={getBoundedInputHandler(59)}
            disabled={disabled}
          />
          <StyledToggleButtonGroup
            value={localTime.period}
            exclusive
            onChange={(e, val) => {
              if (val) handleChange('period', val);
            }}
            disabled={disabled}
          >
            <StyledToggleButton value={KEYS.PERIOD_AM}>{KEYS.PERIOD_AM}</StyledToggleButton>
            <StyledToggleButton value={KEYS.PERIOD_PM}>{KEYS.PERIOD_PM}</StyledToggleButton>
          </StyledToggleButtonGroup>
        </Box>

        <SearchableSelect<SelectItem, false>
          fullWidth
          value={parsed.timezone}
          items={TIMEZONE_OPTIONS}
          onChange={handleTimezoneChange}
          getItemLabel={(item) => item?.label ?? item?.key ?? ''}
          multiple={false}
          error={Boolean(timeTouched && timeError)}
          closeDropdownOnSelect
          disabled={disabled}
        />
      </FormField>
    </Container>
  );
};
